import type { OutboxItem } from '@fruitways/shared';
import { api } from '@/api/endpoints';
import { ApiRequestError, NetworkError } from '@/api/client';
import { db } from './database';

export type DrainResult = { sent: number; remaining: number; stoppedForSignal: boolean };

let draining: Promise<DrainResult> | null = null;

/**
 * Send queued actions in order. Stops at the first item that fails for lack of signal.
 * An item the server rejects for good (4xx other than 401/429) is dropped so it cannot block
 * the queue forever; the estimate row is refreshed from the server on the next pull.
 */
export function drainOutbox(): Promise<DrainResult> {
  if (!draining) {
    draining = doDrain().finally(() => {
      draining = null;
    });
  }
  return draining;
}

async function doDrain(): Promise<DrainResult> {
  const items = await db.outbox.orderBy('seq').toArray();
  let sent = 0;
  for (const item of items) {
    try {
      await sendOne(item);
      await db.outbox.delete(item.id);
      sent++;
    } catch (err) {
      if (err instanceof NetworkError) {
        return { sent, remaining: items.length - sent, stoppedForSignal: true };
      }
      if (
        err instanceof ApiRequestError &&
        (err.status === 401 || err.status === 429 || err.status >= 500)
      ) {
        await db.outbox.update(item.id, { attempts: item.attempts + 1, lastError: err.message });
        return { sent, remaining: items.length - sent, stoppedForSignal: false };
      }
      // Permanent rejection: drop it, keep going.
      await db.outbox.delete(item.id);
      if (err instanceof ApiRequestError && err.status === 404 && item.kind !== 'create') {
        await db.estimates.delete(item.estimateId);
      }
    }
  }
  return { sent, remaining: 0, stoppedForSignal: false };
}

async function sendOne(item: OutboxItem): Promise<void> {
  switch (item.kind) {
    case 'create': {
      const saved = await api.estimates.create(item.payload);
      await db.estimates.put(saved);
      return;
    }
    case 'update': {
      const saved = await api.estimates.update(item.estimateId, item.payload);
      await db.estimates.put(saved);
      return;
    }
    case 'delete':
      await api.estimates.remove(item.estimateId);
      await db.estimates.delete(item.estimateId);
      return;
  }
}

let seqCounter = 0;
/** Append to the outbox. `seq` keeps insertion order even when createdAt ties. */
export async function enqueue(item: OutboxItem): Promise<void> {
  const last = await db.outbox.orderBy('seq').last();
  const seq = Math.max((last?.seq ?? 0) + 1, ++seqCounter);
  await db.outbox.put({ ...item, seq });
}
